import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight, Mail } from 'lucide-react'
import { useAuth } from '@/state/AuthContext'
import { api } from '@/util/api'
import { retryWait } from '@/lib/retryWait'
import { Button, Form, Notice, TextField } from '@/components/play'
import { PublicAuthLayout } from '@/components/public/PublicAuthLayout'
import { SocialSignIn } from '@/components/onboarding/SocialSignIn'
import {
  authLink,
  safeReturnTo,
} from '@/components/onboarding/authContinuation'

type LinkRequestError = {
  response?: {
    status?: number
    data?: { error?: { message?: string; errors?: { email?: string[] } } }
  }
}

const LINK_FAILED = "We couldn't send a link. Try again."
const LINK_OFFLINE = "We couldn't send a link. Check your connection and try again."
const TOO_MANY = 'Too many requests. Try again shortly.'

export function LoginPage() {
  const { login, isAuthenticated, authReady } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo = safeReturnTo(
    searchParams.get('return_to') || searchParams.get('redirect'),
  )
  // The server drops a link's return path into OAuth, so mid-connect (a
  // ChatGPT consent) a link would lose the person's place.
  const offerEmailLink = !returnTo.startsWith('/oauth/')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [sendingLink, setSendingLink] = useState(false)
  // The address a link was asked for. The server answers the same for every
  // address, so this says where the link goes, never that an account exists.
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null)
  const emailInput = useRef<HTMLInputElement>(null)
  const sentNotice = useRef<HTMLDivElement>(null)
  const returningToForm = useRef(false)
  const busy = loading || sendingLink

  useEffect(() => {
    if (emailError) emailInput.current?.focus()
  }, [emailError])

  useEffect(() => {
    if (linkSentTo) {
      sentNotice.current?.focus()
    } else if (returningToForm.current) {
      returningToForm.current = false
      emailInput.current?.focus()
      emailInput.current?.select()
    }
  }, [linkSentTo])

  if (isAuthenticated && authReady) return <Navigate to={returnTo} replace />

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (busy) return
    setError(null)
    setEmailError(null)
    setLoading(true)
    try {
      await login(email.trim(), password)
      navigate(returnTo, { replace: true })
    } catch (err: any) {
      setError(
        err?.response?.data?.error?.message ||
          "We couldn't sign you in. Try again.",
      )
    } finally {
      setLoading(false)
    }
  }

  async function emailMeALink() {
    if (busy) return
    setError(null)
    setEmailError(null)
    const address = email.trim()
    if (!address) {
      setEmailError('Enter your email address.')
      return
    }
    setSendingLink(true)
    try {
      await api.post('/auth/magic_link', { email: address, return_to: returnTo })
      setLinkSentTo(address)
    } catch (err) {
      const response = (err as LinkRequestError)?.response
      const serverError = response?.data?.error
      if (!response) {
        setError(LINK_OFFLINE)
      } else if (response.status === 422) {
        setEmailError(serverError?.errors?.email?.[0] || serverError?.message || 'Enter a valid email address.')
      } else if (response.status === 429) {
        const wait = retryWait(err)
        setError(wait ? `Too many link requests. Try again in ${wait}.` : serverError?.message || TOO_MANY)
      } else {
        setError(serverError?.message || LINK_FAILED)
      }
    } finally {
      setSendingLink(false)
    }
  }

  function changeEmail() {
    returningToForm.current = true
    setLinkSentTo(null)
  }

  return (
    <PublicAuthLayout
      eyebrow="Welcome back"
      title="Back to the action."
      description="Your events, cameras, and live shows. All together."
      backLink={{ to: '/', label: 'StreamBridge' }}
    >
      {linkSentTo ? (
        <div className="space-y-5">
          <Notice ref={sentNotice} tabIndex={-1} tone="success" title="Check your email">
            A sign-in link is on its way to <strong>{linkSentTo}</strong>.
          </Notice>
          <Button type="button" variant="secondary" className="w-full" onClick={changeEmail}>
            Use a different email
          </Button>
        </div>
      ) : (
        // A Play Form: a missing email or password is said beside its field, with focus there, not in the browser's bubble.
        <Form onSubmit={handleSubmit}>
          {error && (
            <Notice tone="error" alert>
              {error}
            </Notice>
          )}
          <TextField
            ref={emailInput}
            label="Email"
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value)
              setEmailError(null)
            }}
            error={emailError ?? undefined}
            required
            requiredMessage="Enter your email address."
            autoFocus
            disabled={busy}
          />
          <TextField
            label="Password"
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            requiredMessage="Enter your password."
            disabled={busy}
          />
          <Button type="submit" className="w-full" loading={loading} disabled={sendingLink}>
            {loading ? 'Signing in' : 'Sign in'}
            <ArrowRight aria-hidden="true" />
          </Button>
          {offerEmailLink && (
            <Button type="button" variant="secondary" className="w-full" onClick={emailMeALink} loading={sendingLink} disabled={loading}>
              <Mail aria-hidden="true" />
              {sendingLink ? 'Sending link' : 'Email me a link'}
            </Button>
          )}
          <SocialSignIn returnTo={returnTo} disabled={busy} />
          <p className="text-center text-sm text-neutral-7">
            First time here?{' '}
            <Link
              to={authLink(
                '/signup',
                returnTo === '/app' ? '/app?create=1' : returnTo,
              )}
              className="font-semibold text-neutral-10 underline underline-offset-4"
            >
              Create account
            </Link>
          </p>
        </Form>
      )}
    </PublicAuthLayout>
  )
}
